import { useState, useEffect } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Archive, RotateCcw, MapPin, Clock, Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  priority: string;
  status: string;
  budget_min?: number;
  budget_max?: number;
  required_date?: string;
  response_by: string;
  created_at: string;
  customer_id: string;
  tradesman_id: string | null;
  job_category?: string;
}

interface ArchivedJob {
  id: string;
  job_id: string;
  reason: string;
  created_at: string;
  jobs?: Job;
}

interface ArchivedJobsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onJobRestored: (job: Job) => void;
  refreshTrigger?: number; // Add this to force refresh from parent
}

export function ArchivedJobsDialog({ isOpen, onClose, onJobRestored, refreshTrigger }: ArchivedJobsDialogProps) {
  const [archivedJobs, setArchivedJobs] = useState<ArchivedJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      fetchCurrentUserProfile();
    }
  }, [isOpen]);

  useEffect(() => {
    if (currentUserProfile?.id && isOpen) {
      fetchArchivedJobs();
    }
  }, [currentUserProfile, isOpen]);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (currentUserProfile?.id && isOpen && refreshTrigger) {
      console.log("Refreshing archived jobs due to refresh trigger:", refreshTrigger);
      fetchArchivedJobs();
    }
  }, [refreshTrigger, currentUserProfile, isOpen]);

  const fetchCurrentUserProfile = async () => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.user.id)
        .single();

      setCurrentUserProfile(profile);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const fetchArchivedJobs = async () => {
    if (!currentUserProfile?.id) {
      console.log("No current user profile available for fetching archived jobs");
      return;
    }
    
    console.log("Fetching archived jobs for profile:", currentUserProfile.id);
    setIsLoading(true);
    try {
      // Use the new security definer function to get archived jobs with job details
      const { data: archivedJobsData, error } = await supabase
        .rpc('get_archived_jobs_for_current_user');

      console.log("Archived jobs RPC result:", { archivedJobsData, error });

      if (error) throw error;

      if (!archivedJobsData || archivedJobsData.length === 0) {
        console.log("No archived jobs found");
        setArchivedJobs([]);
        return;
      }

      // Transform the data to match the expected format
      const transformedData: ArchivedJob[] = archivedJobsData.map(item => ({
        id: item.archive_id,
        job_id: item.job_id,
        profile_id: currentUserProfile.id,
        reason: item.reason,
        created_at: item.archived_at,
        jobs: {
          id: item.job_id,
          title: item.title,
          description: item.description,
          location: item.location,
          status: item.status,
          priority: item.priority,
          budget_min: item.budget_min,
          budget_max: item.budget_max,
          required_date: item.required_date,
          response_by: item.response_by,
          created_at: item.job_created_at,
          job_number: item.job_number,
          job_category: item.job_category,
          required_specialties: item.required_specialties,
          customer_id: item.customer_id || '',
          tradesman_id: item.tradesman_id || null
        } as Job
      }));

      console.log("Transformed archived jobs data:", transformedData);
      setArchivedJobs(transformedData);
    } catch (error: any) {
      console.error("Error loading archived jobs:", error);
      toast({
        title: "Error loading archived jobs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestoreJob = async (archiveId: string, jobTitle: string) => {
    try {
      const { data, error } = await supabase.rpc('restore_job_archive', { p_archive_id: archiveId });
      if (error) throw error;

      // Find the restored job details to optimistically update parent UI
      const restoredJob = archivedJobs.find((a) => a.id === archiveId)?.jobs;

      fetchArchivedJobs(); // Refresh the list
      if (restoredJob) {
        onJobRestored(restoredJob as Job); // Optimistic parent update
      }
    } catch (error: any) {
      toast({
        title: "Error restoring job",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "assigned": return "bg-blue-100 text-blue-800";
      case "in_progress": return "bg-orange-100 text-orange-800";
      case "completed": return "bg-green-100 text-green-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "emergency": return "text-red-600";
      case "urgent": return "text-orange-600";
      case "standard": return "text-blue-600";
      default: return "text-gray-600";
    }
  };

  const formatBudget = (min?: number, max?: number) => {
    if (!min && !max) return "Budget not specified";
    if (min && max) return `£${min} - £${max}`;
    if (min) return `From £${min}`;
    if (max) return `Up to £${max}`;
    return "";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-4xl max-h-[90vh] flex flex-col mx-2 sm:mx-auto">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-muted-foreground" />
            Archived Jobs
          </DialogTitle>
          <DialogDescription>
            View and restore jobs you've previously archived. Click "Restore" to move them back to your active jobs.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading archived jobs...</p>
            </div>
          ) : archivedJobs.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium mb-2">No archived jobs</h3>
              <p className="text-sm text-muted-foreground">
                Jobs you archive will appear here and can be restored at any time.
              </p>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {archivedJobs.map((archivedJob) => {
                const job = archivedJob.jobs;
                if (!job) return null;
                
                return (
                  <Card key={archivedJob.id} className="shadow-sm">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-2xl font-bold text-black flex items-center gap-2">
                            {job.title}
                            <Badge className={getStatusColor(job.status)} variant="secondary">
                              {job.status.replace("_", " ")}
                            </Badge>
                          </CardTitle>
                          <CardDescription className="flex items-center gap-1 mt-1">
                            <MapPin className="h-4 w-4" />
                            {job.location}
                          </CardDescription>
                        </div>
                        <Button
                          onClick={() => handleRestoreJob(archivedJob.id, job.title)}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          <RotateCcw className="h-4 w-4" />
                          Restore
                        </Button>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                       <p className="text-lg text-black font-medium mb-4 line-clamp-2">
                         {job.description}
                       </p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-base text-black font-medium mb-4">
                        <div>
                           <span className="text-black font-medium">Priority:</span>
                           <div className={`font-bold text-black ${getPriorityColor(job.priority)}`}>
                             {job.priority.charAt(0).toUpperCase() + job.priority.slice(1)}
                           </div>
                        </div>
                        
                        <div>
                           <span className="text-black font-medium">Budget:</span>
                           <div className="font-bold text-black">{formatBudget(job.budget_min, job.budget_max)}</div>
                        </div>
                        
                        {job.required_date && (
                          <div>
                             <span className="text-black font-medium">Required:</span>
                             <div className="flex items-center gap-2 text-black">
                               <Calendar className="h-4 w-4" />
                               <span className="font-bold text-base">
                                {format(new Date(job.required_date), "MMM do")}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        <div>
                          <span className="text-muted-foreground">Response by:</span>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span className="font-medium text-xs">
                              {format(new Date(job.response_by), "MMM do, h:mm a")}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <div className="text-sm">
                          <span className="text-muted-foreground">Archived reason: </span>
                          <span className="font-medium">{archivedJob.reason}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Archived on {format(new Date(archivedJob.created_at), "PPP")}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </ScrollArea>
        
        <div className="flex justify-end pt-4 border-t flex-shrink-0">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}